import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import {
  Users, Mail, MessageSquare, Lightbulb, AlertCircle, RefreshCw,
  CheckCircle2, Clock, FileText, Heart, ExternalLink,
} from "lucide-react";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid, Legend,
} from "recharts";
import { Link } from "react-router-dom";
import SecurityIncidentsStrip from "./SecurityIncidentsStrip";

type Range = "24h" | "7d" | "30d" | "90d";
const RANGES: { value: Range; label: string }[] = [
  { value: "24h", label: "24h" },
  { value: "7d", label: "7 days" },
  { value: "30d", label: "30 days" },
  { value: "90d", label: "90 days" },
];

function useAdminQuery(action: string, range: Range, enabled = true) {
  return useQuery({
    queryKey: ["admin-dash", action, range],
    enabled,
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("admin-dashboard-metrics", {
        body: { action, range },
      });
      if (error) throw new Error(error.message);
      if (data?.error) throw new Error(data.error);
      return data;
    },
  });
}

function Kpi({
  icon: Icon, label, value, sub, tone = "default",
}: {
  icon: any; label: string; value: string | number; sub?: string;
  tone?: "default" | "success" | "warn" | "danger";
}) {
  const toneClass = {
    default: "text-primary bg-primary/10",
    success: "text-emerald-500 bg-emerald-500/10",
    warn: "text-amber-500 bg-amber-500/10",
    danger: "text-destructive bg-destructive/10",
  }[tone];
  return (
    <div className="bg-card border border-border rounded-[0.875rem] p-4">
      <div className="flex items-center gap-2 mb-2">
        <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${toneClass}`}>
          <Icon className="h-4 w-4" />
        </div>
        <span className="text-xs text-muted-foreground uppercase tracking-wide">{label}</span>
      </div>
      <div className="font-display text-2xl font-semibold">{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

function Section({ title, children, action }: { title: string; children: React.ReactNode; action?: React.ReactNode }) {
  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-lg font-semibold">{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}

const chartColors = {
  primary: "hsl(var(--primary))",
  muted: "hsl(var(--muted-foreground))",
  destructive: "hsl(var(--destructive))",
};

export default function DashboardTab() {
  const [range, setRange] = useState<Range>("7d");

  const overview = useAdminQuery("overview", range);
  const signups = useAdminQuery("signups_series", range);
  const engagement = useAdminQuery("engagement_series", range);
  const emailSeries = useAdminQuery("email_series", range);
  const topIdeas = useAdminQuery("top_ideas", range);
  const recentSignups = useAdminQuery("recent_signups", range);
  const recentComments = useAdminQuery("recent_comments", range);
  const failures = useAdminQuery("recent_email_failures", range);

  const refreshAll = () => {
    [overview, signups, engagement, emailSeries, topIdeas, recentSignups, recentComments, failures]
      .forEach((q) => q.refetch());
  };

  const o = overview.data;

  return (
    <div className="space-y-8">
      {/* Range picker + refresh */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex gap-1 bg-muted/30 border border-border rounded-lg p-1">
          {RANGES.map((r) => (
            <button
              key={r.value}
              onClick={() => setRange(r.value)}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                range === r.value
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
        <Button variant="outline" size="sm" onClick={refreshAll} disabled={overview.isFetching}>
          <RefreshCw className={`h-4 w-4 mr-2 ${overview.isFetching ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {/* Users */}
      <Section title="Users & signups">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Kpi icon={Users} label="Total users" value={o?.users.total ?? "—"} />
          <Kpi icon={Users} label="New signups" value={o?.users.newSignups ?? "—"} sub={`in last ${range}`} tone="success" />
          <Kpi icon={CheckCircle2} label="Active guests" value={o?.users.roleCounts.guest ?? "—"} />
          <Kpi icon={Clock} label="Expired guests" value={o?.users.expiredGuests ?? "—"} tone="warn" />
        </div>
        <div className="bg-card border border-border rounded-[0.875rem] p-4">
          <h3 className="text-sm font-semibold mb-3">Signups over time</h3>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={signups.data?.series ?? []}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="date" stroke={chartColors.muted} fontSize={11} />
                <YAxis stroke={chartColors.muted} fontSize={11} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))" }} />
                <Line type="monotone" dataKey="count" stroke={chartColors.primary} strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        {o?.users.roleCounts && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {Object.entries(o.users.roleCounts).map(([role, count]) => (
              <div key={role} className="bg-card border border-border rounded-lg p-3">
                <div className="text-xs text-muted-foreground capitalize">{role.replace("_", " ")}</div>
                <div className="font-display text-lg font-semibold">{count as number}</div>
              </div>
            ))}
          </div>
        )}
        <div className="bg-card border border-border rounded-[0.875rem] overflow-hidden">
          <div className="px-4 py-3 border-b border-border text-sm font-semibold">Recent signups</div>
          <div className="divide-y divide-border">
            {(recentSignups.data?.users ?? []).map((u: any) => (
              <div key={u.id} className="px-4 py-2.5 flex items-center justify-between text-sm">
                <div className="truncate">
                  <span className="font-medium">{u.email ?? "—"}</span>
                  <span className="text-xs text-muted-foreground ml-2">{u.provider}</span>
                </div>
                <span className="text-xs text-muted-foreground">{new Date(u.created_at).toLocaleString()}</span>
              </div>
            ))}
            {recentSignups.data && recentSignups.data.users.length === 0 && (
              <div className="px-4 py-6 text-center text-sm text-muted-foreground">No signups yet.</div>
            )}
          </div>
        </div>
      </Section>

      {/* Engagement */}
      <Section title="Content & engagement">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Kpi icon={Lightbulb} label="Total ideas" value={o?.content.totalIdeas ?? "—"} sub={`+${o?.content.ideasInRange ?? 0} in range`} />
          <Kpi icon={Heart} label="Votes" value={o?.content.votes ?? "—"} sub={`in last ${range}`} />
          <Kpi icon={MessageSquare} label="Comments" value={o?.content.comments ?? "—"} sub={`in last ${range}`} />
          <Kpi icon={FileText} label="Published articles" value={o?.content.articlesPublished ?? "—"} />
        </div>
        <div className="bg-card border border-border rounded-[0.875rem] p-4">
          <h3 className="text-sm font-semibold mb-3">Ideas & comments per day</h3>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={engagement.data?.series ?? []}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="date" stroke={chartColors.muted} fontSize={11} />
                <YAxis stroke={chartColors.muted} fontSize={11} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))" }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="ideas" fill={chartColors.primary} radius={[4, 4, 0, 0]} />
                <Bar dataKey="comments" fill={chartColors.muted} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-card border border-border rounded-[0.875rem] overflow-hidden">
            <div className="px-4 py-3 border-b border-border text-sm font-semibold">Top ideas (all-time)</div>
            <div className="divide-y divide-border">
              {(topIdeas.data?.ideas ?? []).map((i: any) => (
                <Link key={i.id} to={`/ideas/${i.id}`} className="px-4 py-2.5 flex items-center justify-between text-sm hover:bg-muted/30 transition-colors">
                  <span className="truncate font-medium">{i.title}</span>
                  <span className="text-xs text-primary ml-3 shrink-0">{i.vote_count} ♥</span>
                </Link>
              ))}
              {topIdeas.data && topIdeas.data.ideas.length === 0 && (
                <div className="px-4 py-6 text-center text-sm text-muted-foreground">No ideas yet.</div>
              )}
            </div>
          </div>
          <div className="bg-card border border-border rounded-[0.875rem] overflow-hidden">
            <div className="px-4 py-3 border-b border-border text-sm font-semibold">Recent comments</div>
            <div className="divide-y divide-border max-h-72 overflow-y-auto">
              {(recentComments.data?.comments ?? []).map((c: any) => (
                <div key={c.id} className="px-4 py-2.5 text-sm">
                  <div className="text-xs text-muted-foreground mb-0.5">
                    on <span className="text-foreground">{c.ideas?.title ?? "—"}</span> · {new Date(c.created_at).toLocaleString()}
                  </div>
                  <div className="line-clamp-2">{c.content}</div>
                </div>
              ))}
              {recentComments.data && recentComments.data.comments.length === 0 && (
                <div className="px-4 py-6 text-center text-sm text-muted-foreground">No comments yet.</div>
              )}
            </div>
          </div>
        </div>
      </Section>

      {/* Email */}
      <Section title="Email delivery">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Kpi icon={Mail} label="Total" value={o?.email.total ?? "—"} sub={`in last ${range}`} />
          <Kpi icon={CheckCircle2} label="Sent" value={o?.email.sent ?? "—"} tone="success" />
          <Kpi icon={AlertCircle} label="Failed" value={o?.email.failed ?? "—"} tone="danger" />
          <Kpi icon={Clock} label="Pending" value={o?.email.pending ?? "—"} tone="warn" />
          <Kpi icon={AlertCircle} label="Suppressed" value={o?.email.suppressed ?? "—"} />
        </div>
        <div className="bg-card border border-border rounded-[0.875rem] p-4">
          <h3 className="text-sm font-semibold mb-3">Email sends per day</h3>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={emailSeries.data?.series ?? []}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="date" stroke={chartColors.muted} fontSize={11} />
                <YAxis stroke={chartColors.muted} fontSize={11} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))" }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="sent" stackId="a" fill={chartColors.primary} />
                <Bar dataKey="failed" stackId="a" fill={chartColors.destructive} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="bg-card border border-border rounded-[0.875rem] overflow-hidden">
          <div className="px-4 py-3 border-b border-border text-sm font-semibold flex items-center gap-2">
            <AlertCircle className="h-4 w-4 text-destructive" />
            Recent failures
          </div>
          <div className="divide-y divide-border max-h-80 overflow-y-auto">
            {(failures.data?.failures ?? []).map((f: any) => (
              <div key={f.id} className="px-4 py-2.5 text-sm">
                <div className="flex items-center justify-between gap-3">
                  <span className="font-medium truncate">{f.template_name}</span>
                  <span className="text-xs text-muted-foreground shrink-0">{new Date(f.created_at).toLocaleString()}</span>
                </div>
                <div className="text-xs text-muted-foreground truncate">→ {f.recipient_email}</div>
                {f.error_message && (
                  <div className="text-xs text-destructive mt-0.5 line-clamp-2">{f.error_message}</div>
                )}
              </div>
            ))}
            {failures.data && failures.data.failures.length === 0 && (
              <div className="px-4 py-6 text-center text-sm text-muted-foreground">
                No email failures — nice. ✓
              </div>
            )}
          </div>
        </div>
      </Section>

      {/* Health strip */}
      <Section title="System health">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <a
            href="/status"
            target="_blank"
            rel="noreferrer"
            className="bg-card border border-border rounded-[0.875rem] p-4 hover:border-primary/40 transition-colors flex items-center justify-between"
          >
            <div>
              <div className="text-xs text-muted-foreground uppercase tracking-wide">Public status</div>
              <div className="font-display font-semibold mt-1">BetterStack monitoring</div>
            </div>
            <ExternalLink className="h-4 w-4 text-muted-foreground" />
          </a>
          <div className="bg-card border border-border rounded-[0.875rem] p-4">
            <div className="text-xs text-muted-foreground uppercase tracking-wide">Site analytics</div>
            <div className="text-sm mt-1 text-muted-foreground">
              View page-view and session metrics in the Lovable project analytics panel.
            </div>
          </div>
          <div className="bg-card border border-border rounded-[0.875rem] p-4">
            <div className="text-xs text-muted-foreground uppercase tracking-wide">Financials</div>
            <div className="text-sm mt-1 text-muted-foreground">
              No payment provider connected yet. Enable Stripe to track revenue here.
            </div>
          </div>
        </div>
      </Section>

      {/* Security & incidents strip */}
      <SecurityIncidentsStrip />

      {overview.error && (
        <div className="bg-destructive/10 border border-destructive/30 text-destructive text-sm rounded-lg p-3">
          {(overview.error as Error).message}
        </div>
      )}
    </div>
  );
}